<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import inboxSignaturesAPI from 'dashboard/api/inboxSignatures';

const props = defineProps({
  messageSignature: { type: String, default: '' },
  signaturePosition: { type: String, default: 'bottom' },
  signatureSeparator: { type: String, default: 'blank' },
});

const emit = defineEmits(['updateSignature']);
const store = useStore();
const { t } = useI18n();

const selectedInboxId = ref(null); // null = Padrão (global)
const signature = ref(props.messageSignature);
const position = ref(props.signaturePosition);
const separator = ref(props.signatureSeparator);
const isSaving = ref(false);

const inboxes = computed(() => store.getters['inboxes/getInboxes']);

const positionOptions = computed(() => [
  { value: 'top', label: t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_TOP') },
  { value: 'bottom', label: t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_BOTTOM') },
]);

const separatorOptions = computed(() => [
  { value: 'blank', label: t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SEPARATOR_BLANK') },
  { value: '--', label: t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SEPARATOR_DASHES') },
]);

// Reset to global defaults when switching back to Padrão
watch(selectedInboxId, async (newInboxId) => {
  if (newInboxId === null) {
    signature.value = props.messageSignature;
    position.value = props.signaturePosition;
    separator.value = props.signatureSeparator;
    return;
  }
  try {
    const { data } = await inboxSignaturesAPI.get(newInboxId);
    signature.value = data.message_signature ?? '';
    position.value = data.signature_position ?? 'bottom';
    separator.value = data.signature_separator ?? 'blank';
  } catch {
    // No inbox signature yet — start fresh with current defaults
    signature.value = props.messageSignature;
    position.value = props.signaturePosition;
    separator.value = props.signatureSeparator;
  }
});

watch(() => props.messageSignature, (val) => {
  if (selectedInboxId.value === null) signature.value = val ?? '';
});
watch(() => props.signaturePosition, (val) => {
  if (selectedInboxId.value === null) position.value = val ?? 'bottom';
});
watch(() => props.signatureSeparator, (val) => {
  if (selectedInboxId.value === null) separator.value = val ?? 'blank';
});

const save = async () => {
  isSaving.value = true;
  try {
    if (selectedInboxId.value === null) {
      emit('updateSignature', {
        message_signature: signature.value,
        signature_position: position.value,
        signature_separator: separator.value,
      });
    } else {
      await inboxSignaturesAPI.upsert(selectedInboxId.value, {
        message_signature: signature.value,
        signature_position: position.value,
        signature_separator: separator.value,
      });
      useAlert(t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'));
    }
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="save">
    <!-- Inbox selector -->
    <div class="flex gap-4">
      <div class="flex flex-col gap-1 flex-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INBOX_LABEL') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option :value="null">
            {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INBOX_DEFAULT') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1 flex-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_LABEL') }}
        </label>
        <select
          v-model="position"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option v-for="opt in positionOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1 flex-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SEPARATOR_LABEL') }}
        </label>
        <select
          v-model="separator"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option v-for="opt in separatorOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Signature editor -->
    <WootMessageEditor
      id="message-signature-input"
      v-model="signature"
      class="message-editor h-[10rem] !px-3"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
      show-image-resize-toolbar
    />

    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
        :loading="isSaving"
      />
    </div>

    <!-- Preview -->
    <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
      <p class="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3 flex items-center gap-2">
        <span class="i-ri-eye-line" />
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW_TITLE') }}
      </p>
      <div class="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-700 dark:text-slate-200">
        <!-- position = top: signature first, then message -->
        <template v-if="position === 'top'">
          <div v-if="signature" class="mb-2 border-b border-slate-100 dark:border-slate-700 pb-2" v-html="signature" />
          <div v-if="separator === '--'" class="text-slate-400 text-xs mb-2">--</div>
          <p>{{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW_SAMPLE') }}</p>
        </template>
        <!-- position = bottom: message first, then signature -->
        <template v-else>
          <p class="mb-2">{{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW_SAMPLE') }}</p>
          <div v-if="separator === '--'" class="text-slate-400 text-xs mt-2 mb-1">--</div>
          <div v-else class="mt-2" />
          <div v-if="signature" v-html="signature" />
        </template>
      </div>
      <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW_NOTE') }}
      </p>
    </div>
  </form>
</template>
